import { useState } from "react";
import { constrainToSegmentBounds, findClosestSegment, haversineDistanceMeters, projectPointOnLine, createPerpendicularPoint, validateCustomIntersectPoint } from "../../utils/locationUtils";
import { getUSandDSAgms, getUSandDSDists } from "../../utils/poi/poiHookHelpers";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { useError } from "@contexts/ErrorContext";

/**
 * Handles marker related events, updates state from various poi hooks accordingly
 * 
 * @param {Object} state - state from other files: currentClickedPoint and currentLine from usePoiMap, lines and clickedPoints from usePoiService
 * @param {Object} setters - setter functions from other files: setCurrentClickedPoint, setCurrentLine, setIwData, setActiveComponent from usePoiMap, setLines and clickedPoints from usePoiServices
 * @param {Object} polylineManager - all the functions to update map polylines from usePolylineManager
 * @param {Function} resetLines - resets state and lines, from usePoiService
 * @param {Function} removeUnsavedClickedPoint - from usePoiService
 * 
 * @returns {Object} draggableMarkerPosition {lat: float, lng: float}
 * @returns {Line} lineBeforeDrag - if the marker is dragged but not saved, revert back to this line
 * @returns {Function} setLineBeforeDrag
 * @returns {Function} handleMarkerDrag
 * @returns {Function} resetMarkerState
 * @returns {Function} setDraggableMarkerPosition
 * @returns {Function} handleMarkerClick
 * @returns {Function} handleMapClick
 * 
 * @category Hooks - POI Map
 */
const useMarkerManager = ({
        state: {currentClickedPoint, currentLine, lines, clickedPoints, selectedPoiGroup},
        setters: {setCurrentClickedPoint, setCurrentLine, setIwData, setLines, setClickedPoints, setActiveComponent},
        polylineManager,
        resetLines,
        removeUnsavedClickedPoint
}) => {
    const [draggableMarkerPosition, setDraggableMarkerPosition] = useState(null);
    const [lineBeforeDrag, setLineBeforeDrag] = useState(null);

    const {hasUnsavedChanges, setHasUnsavedChanges, requestConfirm} = useUnsavedChanges();
    const {showError} = useError()

    const currentPage = window.location.pathname

    const handleMarkerDrag = (event, polyLineArr) => {

        if(!lineBeforeDrag){
            setLineBeforeDrag(currentLine)
            setHasUnsavedChanges(true)
        }

        const newPosition = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
        };

        const closestSegment = findClosestSegment(newPosition, polyLineArr);
        const constrainedPosition = projectPointOnLine(
            newPosition,
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream
        );

        const finalPosition = constrainToSegmentBounds(
            constrainedPosition,
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream
        );

        setDraggableMarkerPosition(finalPosition);

        let upstreamAndDownstreamAgms = getUSandDSAgms(closestSegment, polyLineArr);
        const poiMetersToPipeline = haversineDistanceMeters(currentClickedPoint.lat, currentClickedPoint.lng, finalPosition.lat, finalPosition.lng);
        let usDsDistances = getUSandDSDists(upstreamAndDownstreamAgms.upstreamAgm, upstreamAndDownstreamAgms.downstreamAgm, finalPosition);

        setIwData((prevData) => ({
            ...prevData,
            poiMetersToPipeline,
            lineIntersect: finalPosition,
            upstreamAndDownstreamAgms,
            usDsDistances,
            poiGroups: currentClickedPoint.poiGroups,
            CreatedBy: currentClickedPoint.CreatedBy,
            linkedPOIs: currentClickedPoint.linkedPOIs,

            //these fields will only be in poi if pois were fetched by group
            ...(currentClickedPoint.AltPOI_Name && { AltPOI_Name: currentClickedPoint.AltPOI_Name }),
            ...(currentClickedPoint.AltDescription && { AltDescription: currentClickedPoint.AltDescription }),
            ...(currentClickedPoint.AltCreatedBy && { AltCreatedBy: currentClickedPoint.AltCreatedBy }),
            ...(currentClickedPoint.LPOIID && { LPOIID: currentClickedPoint.LPOIID })
        }));
        
        polylineManager.clearCurrentPolyline();

        updateCurrentLineState({ start: currentClickedPoint, end: finalPosition });

        polylineManager.addCurrentPolyline({ start: currentClickedPoint, end: finalPosition });
    };

    /********************************************/
    //Clicking a previous set poi point
    /********************************************/
    const handleMarkerClick = async(point, polyLineArr) => {
        //check if the marker that was clicked is the current point
        //if it is, this can cause issues with the current poly line, so just return
        if(currentClickedPoint && point.POIID === currentClickedPoint.POIID) return

        //if there is unsaved work, ask user to confirm if they want to continue
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm("The previous POI has unsaved work - are you sure you want to cancel?")
        
            if(!isConfirmed) return
        }

        //reset lines on map and update current line and marker
        if(Object.keys(currentLine).length > 0 && currentLine.start.POIID){
            //if there is already a green line on the map, and the POI is saved in db, re-add line back into lines state
            const addLineToState = lineBeforeDrag ?? currentLine
            resetLines(polylineManager, addLineToState)
        } else{
            resetLines(polylineManager)
        }
        
        //just want to remove unsaved points, important to ensure currentClickedPoint is updated when POI saved, or that dialog box is closed
        removeUnsavedClickedPoint(currentClickedPoint)

        let index = lines.findIndex(line => line.start.lineID === point.lineID);

        //if index is -1, this line can't be found
        //TODO: we are already checking above if newly clicked point is current point, do we still need this check
        if(index === -1) return

        setLineBeforeDrag(null)
        setDraggableMarkerPosition(lines[index].end);
        
        setCurrentClickedPoint(
            {
                lat: point.lat,
                lng: point.lng,
                lineID: point.lineID,
                POIID: point.POIID,
                POI_Name: point.POI_Name,
                Description: point.Description,
                poiGroups: point.poiGroups,
                CreatedBy: point.CreatedBy,
                linkedPOIs: point.linkedPOIs,

                //these fields will only be in poi if pois were fetched by group
                ...(point.AltPOI_Name && { AltPOI_Name: point.AltPOI_Name }),
                ...(point.AltDescription && { AltDescription: point.AltDescription }),
                ...(point.AltCreatedBy && { AltCreatedBy: point.AltCreatedBy }),
                ...(point.LPOIID && { LPOIID: point.LPOIID })
            }
        );
        
        updateCurrentLineState(lines[index]);

        polylineManager.addCurrentPolyline(lines[index]);

        setLines(prevLines => {
            let updatedArr = prevLines.filter((line) => line.start.lineID !== point.lineID);
            return [...updatedArr];
        });

        const poiMetersToPipeline = haversineDistanceMeters(lines[index].start.lat, lines[index].start.lng, lines[index].end.lat, lines[index].end.lng);
        
        const lineIntersect = {
            lat: lines[index].end.lat, 
            lng: lines[index].end.lng
        }

        const closestSegment = findClosestSegment(lineIntersect, polyLineArr);
        
        let upstreamAndDownstreamAgms = getUSandDSAgms(closestSegment, polyLineArr);
        let usDsDistances = getUSandDSDists(upstreamAndDownstreamAgms.upstreamAgm, upstreamAndDownstreamAgms.downstreamAgm, lineIntersect);

        //reset active dialog box component
        setActiveComponent("PoiDialog")

        //reset iwData when marker is clicked, based on 'point'
        setIwData({
            POI_Name: point.POI_Name,
            Description: point.Description,
            POIID: point.POIID,
            poiMetersToPipeline,
            usDsDistances,
            upstreamAndDownstreamAgms,
            lineIntersect,
            lat: point.lat,
            lng: point.lng,
            poiGroups: point.poiGroups,
            CreatedBy: point.CreatedBy,
            linkedPOIs: point.linkedPOIs,

            //these fields will only be in poi if pois were fetched by group
            ...(point.AltPOI_Name && { AltPOI_Name: point.AltPOI_Name }),
            ...(point.AltDescription && { AltDescription: point.AltDescription }),
            ...(point.AltCreatedBy && { AltCreatedBy: point.AltCreatedBy }),
            ...(point.LPOIID && { LPOIID: point.LPOIID })
        });

    }

    /***********************************************************/
    //Open a single POI on the map - user selected it from table
    /***********************************************************/
    
    //TODO: a lot of similar code between here and handleMarkerClick - could any of this be condensed?

    const handleOpenPoiFromTable = async(point, polyLineArr, lines) => {

        //reset lines on map and update current line and marker
        if(Object.keys(currentLine).length > 0 && currentLine.start.POIID){
            //if there is already a green line on the map, and the POI is saved in db, re-add line back into lines state
            const addLineToState = lineBeforeDrag ?? currentLine
            resetLines(polylineManager, addLineToState)
        } else{
            resetLines(polylineManager)
        }

        let index = lines.findIndex(line => line.start.lineID === point.lineID);

        //if index is -1, this line can't be found
        //TODO: we are already checking above if newly clicked point is current point, do we still need this check
        if(index === -1) return

        setLineBeforeDrag(null)
        setDraggableMarkerPosition(lines[index].end);
        
        setCurrentClickedPoint(
            {
                lat: point.lat,
                lng: point.lng,
                lineID: point.lineID,
                POIID: point.POIID,
                Description: point.Description,
                POI_Name: point.POI_Name,
                poiGroups: point.poiGroups,
                CreatedBy: point.CreatedBy,
                linkedPOIs: point.linkedPOIs,

                //these fields will only be in poi if pois were fetched by group
                ...(point.AltPOI_Name && { AltPOI_Name: point.AltPOI_Name }),
                ...(point.AltDescription && { AltDescription: point.AltDescription }),
                ...(point.AltCreatedBy && { AltCreatedBy: point.AltCreatedBy }),
                ...(point.LPOIID && { LPOIID: point.LPOIID })
            }
        );
        
        updateCurrentLineState(lines[index]);

        polylineManager.addCurrentPolyline(lines[index]);

        setLines(prevLines => {
            let updatedArr = prevLines.filter((line) => line.start.lineID !== point.lineID);
            return [...updatedArr];
        });

        const poiMetersToPipeline = haversineDistanceMeters(lines[index].start.lat, lines[index].start.lng, lines[index].end.lat, lines[index].end.lng);
        
        const lineIntersect = {
            lat: lines[index].end.lat, 
            lng: lines[index].end.lng
        }

        const closestSegment = findClosestSegment(lineIntersect, polyLineArr);
        
        let upstreamAndDownstreamAgms = getUSandDSAgms(closestSegment, polyLineArr);
        let usDsDistances = getUSandDSDists(upstreamAndDownstreamAgms.upstreamAgm, upstreamAndDownstreamAgms.downstreamAgm, lineIntersect);

        //reset active dialog box component
        setActiveComponent("PoiDialog")

        //reset iwData when marker is clicked, based on 'point'
        setIwData({
            POI_Name: point.POI_Name,
            Description: point.Description,
            POIID: point.POIID,
            poiMetersToPipeline,
            usDsDistances,
            upstreamAndDownstreamAgms,
            lineIntersect,
            lat: point.lat,
            lng: point.lng,
            poiGroups: point.poiGroups,
            CreatedBy: point.CreatedBy,
            linkedPOIs: point.linkedPOIs,

            //these fields will only be in poi if pois were fetched by group
            ...(point.AltPOI_Name && { AltPOI_Name: point.AltPOI_Name }),
            ...(point.AltDescription && { AltDescription: point.AltDescription }),
            ...(point.AltCreatedBy && { AltCreatedBy: point.AltCreatedBy }),
            ...(point.LPOIID && { LPOIID: point.LPOIID })
        });

    }

    /********************************************/
    //Clicking on the map - add a POI
    /********************************************/
    const handleMapClick = async(event, polyLineArr) => {
        if(currentPage.includes("site-map")) return

        if(
            (selectedPoiGroup && !selectedPoiGroup.label.includes("All POIs")) &&
            (!selectedPoiGroup.IsUserInGroup || !selectedPoiGroup.IsEditor)
        ){
            showError(`To create POIs in the group "${selectedPoiGroup.label}", you must be an editor. Please select a group in which you are an editor, or select "All POIs for job" to create POIs.`)
            return   
        }
        
        //if there is unsaved work, ask user to confirm if they want to continue
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm("The previous POI has unsaved work - are you sure you want to cancel?")
        
            if(!isConfirmed) return
        }

        //reset lines on map and update current line and marker
        if(Object.keys(currentLine).length > 0 && currentLine.start.POIID){
            //if there is already a green line on the map, and the POI is saved in db, re-add line back into lines state
            const addLineToState = lineBeforeDrag ?? currentLine
            resetLines(polylineManager, addLineToState)
        } else{
            resetLines(polylineManager)
        }
        
        removeUnsavedClickedPoint(currentClickedPoint)

        const lineID = Math.random() * 10000; //new line ID for filtering

        //this is the new point clicked on the map
        //lineID for filtering - POI_Name for hover label
        let clickedPoint = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
            lineID: lineID,
            POI_Name: `POI-${clickedPoints.length + 1}`,
            POIID: null,
            poiGroups: [],
            linkedPOIs: []
        };

        setCurrentClickedPoint(clickedPoint);

        //Get the US and DS site - will return a sc
        const closestSegment = findClosestSegment(clickedPoint, polyLineArr);

        //add the point to array of clicked points (create yellow flag)
        setClickedPoints(prevPoints => [...prevPoints, clickedPoint]);
        
        //find closest point to pipeline
        let lineIntersect = projectPointOnLine(clickedPoint, closestSegment.closestSegment.upstream, closestSegment.closestSegment.downstream)

        //distance from clicked point to pipeline
        const poiMetersToPipeline = haversineDistanceMeters(clickedPoint.lat, clickedPoint.lng, lineIntersect.lat, lineIntersect.lng);

        //create the draggable marker(blue circle)
        setDraggableMarkerPosition(lineIntersect);

        updateCurrentLineState({ start: clickedPoint, end: lineIntersect });

        polylineManager.addCurrentPolyline({ start: clickedPoint, end: lineIntersect });

        let upstreamAndDownstreamAgms = getUSandDSAgms(closestSegment, polyLineArr);
        let usDsDistances = getUSandDSDists(upstreamAndDownstreamAgms.upstreamAgm, upstreamAndDownstreamAgms.downstreamAgm, lineIntersect);

        //data for Info Window
        let clickedPointData = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
            POI_Name: `POI-${clickedPoints.length + 1}`,
            POIID: clickedPoint.POIID,
            poiMetersToPipeline,
            upstreamAndDownstreamAgms,
            usDsDistances,
            lineIntersect,
            poiGroups: clickedPoint.poiGroups,
            CreatedBy: clickedPoint.CreatedBy,
            linkedPOIs: clickedPoint.linkedPOIs,

        }

        //reset active dialog box component
        setActiveComponent("PoiDialog")
        
        setIwData(clickedPointData);

        //setting a new point and line, so there is unsaved work on the map
        setHasUnsavedChanges(true)
    };

    /********************************************/
    //User enters their own intersection point without clicking on map
    /********************************************/
    const handleCreatePointFromCustomIntersect = async(lineIntersect, polyLineArr) => {
        // Basic input validation
        if (!lineIntersect || typeof lineIntersect !== 'object') {
            return { success: false, errorMessage: 'Invalid intersection coordinates' };
        }

        const lat = Number(lineIntersect.lat);
        const lng = Number(lineIntersect.lng);

        if (!isFinite(lat) || !isFinite(lng)) {
            return { success: false, errorMessage: 'Intersection coordinates must be finite numbers' };
        }

        if (!Array.isArray(polyLineArr) || polyLineArr.length === 0) {
            return { success: false, errorMessage: 'AGMs and subchains not available' };
        }

        const closestSegment = findClosestSegment({ lat, lng }, polyLineArr);

        if (!closestSegment || !closestSegment.closestSegment) {
            return { success: false, errorMessage: 'No nearby pipeline segment found' };
        }

        //check how far away it is from pipeline
        const validation = validateCustomIntersectPoint(
            { lat, lng },
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream,
            10
        );

        if (!validation.isValid) {
            // Return validation error so modal can display it
            return {
                success: false,
                errorMessage: validation.errorMessage
            };
        }

        //reset lines on map and update current line and marker
        if(Object.keys(currentLine).length > 0 && currentLine.start.POIID){
            //if there is already a green line on the map, and the POI is saved in db, re-add line back into lines state
            const addLineToState = lineBeforeDrag ?? currentLine
            resetLines(polylineManager, addLineToState)
        } else{
            resetLines(polylineManager)
        }
        
        removeUnsavedClickedPoint(currentClickedPoint)

        const generatedMapPoint = createPerpendicularPoint(
            { lat, lng },
            closestSegment.closestSegment.upstream,
            closestSegment.closestSegment.downstream,
            10  // 10 meters offset
        );

        generatedMapPoint.lineID = Math.random() * 10000;
        generatedMapPoint.POI_Name = `POI-${clickedPoints.length + 1}`;
        generatedMapPoint.POIID = null;
        generatedMapPoint.poiGroups = [];
        generatedMapPoint.linkedPOIs = [];

        setCurrentClickedPoint(generatedMapPoint);

        //add the point to array of clicked points (create yellow flag)
        setClickedPoints(prevPoints => [...prevPoints, generatedMapPoint]);
        
        //distance from clicked point to pipeline
        const poiMetersToPipeline = haversineDistanceMeters(generatedMapPoint.lat, generatedMapPoint.lng, lat, lng);

        //create the draggable marker(blue circle)
        setDraggableMarkerPosition({ lat, lng });

        updateCurrentLineState({ start: generatedMapPoint, end: { lat, lng } });

        polylineManager.addCurrentPolyline({ start: generatedMapPoint, end: { lat, lng } });

        let upstreamAndDownstreamAgms = getUSandDSAgms(closestSegment, polyLineArr);
        let usDsDistances = getUSandDSDists(upstreamAndDownstreamAgms.upstreamAgm, upstreamAndDownstreamAgms.downstreamAgm, { lat, lng });

        //data for Info Window
        let clickedPointData = {
            lat: generatedMapPoint.lat,
            lng: generatedMapPoint.lng,
            POI_Name: `POI-${clickedPoints.length + 1}`,
            POIID: generatedMapPoint.POIID,
            poiMetersToPipeline,
            upstreamAndDownstreamAgms,
            usDsDistances,
            lineIntersect,
            poiGroups: generatedMapPoint.poiGroups,
            CreatedBy: generatedMapPoint.CreatedBy,
            linkedPOIs: generatedMapPoint.linkedPOIs,

        }

        //reset active dialog box component
        setActiveComponent("PoiDialog")
        
        setIwData(clickedPointData);

        //setting a new point and line, so there is unsaved work on the map
        setHasUnsavedChanges(true)

        return {
            success: true
        }
    };

    const resetMarkerState = () => {
        setDraggableMarkerPosition(null);
        setLineBeforeDrag(null);
    };

    const updateCurrentLineState = (newLine) => {
        setCurrentLine(newLine)
    };

    return {
        draggableMarkerPosition,
        lineBeforeDrag,
        setLineBeforeDrag,
        handleMarkerDrag,
        resetMarkerState,
        setDraggableMarkerPosition,
        handleMarkerClick,
        handleOpenPoiFromTable,
        handleMapClick,
        handleCreatePointFromCustomIntersect
    };


};

export default useMarkerManager;
